import React, { useEffect, useRef } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { PrimeReactProvider } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { LayoutProvider } from './layout/context/layoutcontext';
import { MenuProvider } from './layout/context/menucontext';
import Layout from './layout/layout';
import FullPageLayout from './layouts/FullPageLayout';
import { store } from './store';
import { toastService } from './utils/toastService';
import PrivateRoute from './components/auth/PrivateRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { logout, selectIsAuthenticated, selectIsTokenExpired } from './features/auth/authSlice';

// i18n configuration - Import this BEFORE any other imports
import './i18n';

// Language Context Import
import { LanguageProvider } from './contexts/LanguageContext';
import { MenuCategoryProvider } from './contexts/MenuCategoryContext';
import Login from './features/auth/components/Login';
import ForgotPassword from './features/auth/components/ForgotPassword';
import ResetPassword from './features/auth/components/ResetPassword';
import MenuCategory from './pages/MenuCategory';
import NotFound from './pages/NotFound';
import ForbiddenPage from './pages/403';
import BrandPage from './pages/management/model/Brand';
import AnnouncementPage from './pages/management/announcement/index';
import DealerAnnouncementsPage from './pages/management/announcement/dealer-announcements/index';
import AnnouncementTypePage from './pages/management/announcement-type/AnnouncementType';
import ModelPage from './pages/management/model/Model';
import DealerRegistrationPage from './pages/management/customer/dealerRegistration';
import DealerRegistrationEditPage from './pages/management/customer/dealerRegistration/DealerRegistrationEditPage';
import DealerApprovalInboxPage from './pages/management/dealerApproval/inbox';
import DealerApprovalDetailPage from './pages/management/dealerApproval/detail';

import CustomerAddressPricePage from './pages/management/customer/customerAddressPrice/index';
import CustomerAddressReportPage from './pages/reports/CustomerAddressReport';
import EstimatedDeliveryDatePage from './pages/reports/EstimatedDeliveryDate';
import OrderControlReportPage from './pages/reports/OrderControlReport';
import OrderSummaryPage from './pages/reports/OrderSummary';
import PartsBalanceListPage from './pages/reports/PartsBalanceList';
import ShipmentsDeliveriesPage from './pages/reports/ShipmentsDeliveries';
import TopPricePartsReport from './pages/reports/TopPricePartsReport';
import DailyInactiveVehicleReport from './pages/reports/DailyInactiveVehicleReport';
import FeedbackFormReportPage from './pages/quality/feedback-form-report';
import CustomerOrderTypePage from './pages/management/customer/customerOrderType/index';
import CustomerDefinitionPage from './pages/warranty-management/customer-definition';
import SystemItemsPage from './pages/management/system-definitions/SystemItems';
import SystemAdministratorPage from './pages/management/user-definitions/system-administrator';
import UserAccountsPage from './pages/management/user-definitions/user-accounts';
import SuperAdminDefinitionPage from './pages/management/user-definitions/super-admin-definition';
import AuthorizationV2RolesPage from './pages/management/authorization-v2/role-management';
import AuthorizationV2UsersPage from './pages/management/authorization-v2/user-access';
import UserParams from './pages/UserParams';
import PreApprovalPage from './pages/warranty-management/warranty-requests/preApproval/index';
import WarrantyClaims from './pages/warranty-management/warranty-requests/warranty-claims';
import WarrantyClaimsCreateEdit from './pages/warranty-management/warranty-requests/warranty-claims/createEditForm/WarrantyClaimsCreateEdit';
// import ServiceAppointmentPage from './pages/service-appointment/index'; // Servis Randevusu geçici olarak kapatıldı
import ProformaInvoicePage from './pages/proforma-invoice/index';
import ProformaInvoiceDetailPage from './pages/proforma-invoice/detail/index';
import VehicleAcceptanceFormPage from './pages/warranty-management/warranty-process-forms/vehicle-acceptance-form';
import FaultCodesDefinitionsPage from './pages/warranty-management/fault-codes';
import PreStockInspectıonChecklistPage from './pages/warranty-management/warranty-process-forms/pre-stock-inspection-checklist';
import FormPreStockInspection from './pages/warranty-management/warranty-process-forms/pre-stock-inspection-checklist/createEditForm/PreStockInspectionCreateEdit';
import DealerVehicleRegistrationFormPage from './pages/warranty-management/warranty-process-forms/dealer-vehicle-registration-form';
import WarrantyDateChangeApprovalPage from './pages/warranty-management/warranty-process-forms/warranty-date-change-approval';
import VehicleWarrantyPeriodPage from './pages/warranty-management/vehicle-warranty-period/main';
import VehicleAdditionalWarrantyPeriodPage from './pages/warranty-management/vehicle-warranty-period/additional';
import WarrantyClaimLimitPage from './pages/warranty-management/vehicle-warranty-period/claim-limit';
import VehicleDetailQueryPage from './pages/warranty-management/vehicle-detail-information/index';
import VehicleRegistrationFormCreateEdit from './pages/warranty-management/warranty-process-forms/dealer-vehicle-registration-form/createEditForm/VehicleRegistrationFormCreateEdit';
import OrderListPage from './pages/order-management/order-list';
import OrderDetails from './pages/order-management/order-details/OrderDetails';
import OrderListCreateEdit from './pages/order-management/order-list/createEditForm/OrderListCreateEdit';
import ShipmentDeliveryPage from './pages/shipments/ShipmentDeliveryPage';
import CustomerDeclarationPage from './pages/warranty-management/customer-declaration';
import LaborCatalogPage from './pages/warranty-management/warranty-settings/labor-catalog';
import FaultCustomerDefinitionsPage from './pages/warranty-management/warranty-settings/fault-customer-definitions';
import ApprovalGroupsPage from './pages/management/warrantyapprovalmanagement/approval-groups';
import ApprovalLimitsPage from './pages/management/warrantyapprovalmanagement/approval-limits';
import WaybillShipmentPage from './pages/parts/waybillShipment';
import OrderPickUpPage from './pages/parts/orderPickup';
import OrderBalancePage from './pages/parts/OrderBalanceCollection';
import CreditCardCollectionPage from './pages/parts/creditCardCollection';
import RetailSalesPage from './pages/parts/retailSales';
import PlaceOrderPage from './pages/vehicleSales/placeOrder';
import OrderRequestsPage from './pages/vehicleSales/orderRequests';
import MyVehicleStockPage from './pages/vehicleSales/myVehicleStock';
import SellToCustomerPage from './pages/vehicleSales/sellToCustomer';
import VehiclesForSalePage from './pages/vehicleSales/vehiclesForSale';
import RefundOrdersPage from './pages/vehicleSales/refundOrders';
import InternalRetailPartsPage from './pages/parts/internalRetailParts';
import UnpickedPartsReportPage from './pages/parts/unpickedPartsReport';
import PaymentSuccess from './pages/parts/creditCardCollection/PaymentSuccess';
import PaymentFailure from './pages/parts/creditCardCollection/PaymentFailure';
import PaymentResult from './pages/parts/creditCardCollection/PaymentResult';
import ProductSearchPage from './pages/parts/productSearch';
import BasketPage from './pages/basket';
import DealerWarehousePage from './pages/dealerWarehouse';
import FullServiceInvoicePage from './pages/fullServiceInvoice';
import FullServiceCreateEditPage from './pages/fullServiceInvoice/createEditForm/FullServiceCreateEditPage';
import DealerStockReportPage from './pages/dealerStockReport';
import DealerPartsReturnProcessPage from './pages/management/dealerPartsReturnProcess';
import InvoiceApprovalPage from './pages/warranty-management/accounting-process/invoice-approval';
import EditPartLaborsPage from './pages/warranty-management/part-labors';
import PartLaborMappingPage from './pages/warranty-management/part-labors';
import DealerUserAccountPage from './pages/management/dealerUserDefinition';
import PackageDefinitionsPage from './pages/packageDefinitions';
import WarrantyReportsPage from './pages/warranty-management/warranty-reports';
import WarrantyDashboardPage from './pages/warranty-management/dashboard';
import MyTrainingsPage from './pages/learning-management-system/my-trainings';
import LmsManagementPagesPage from './pages/learning-management-system/management-pages';
import TrainingTypeDefinitionsPage from './pages/learning-management-system/training-type-definitions';
import TrainingLocationDefinitionsPage from './pages/learning-management-system/training-location-definitions';
import PreconditionDefinitionsPage from './pages/learning-management-system/precondition-definitions';
import VehicleModelDefinitionsPage from './pages/learning-management-system/vehicle-model-definitions';
import TrainingCatalogPage from './pages/learning-management-system/training-catalog';
import TrainingCatalogCreateEdit from './pages/learning-management-system/training-catalog/createEditForm/TrainingCatalogCreateEdit';
import TrainingPlanningPage from './pages/learning-management-system/training-planning';
import TrainingReportPage from './pages/learning-management-system/training-report';
import TrainingReportCreateEdit from './pages/learning-management-system/training-report/createEditForm/TrainingReportCreateEdit';
import TrainingRequestPage from './pages/learning-management-system/training-request';
import VehicleTrainingMatchPage from './pages/learning-management-system/vehicle-training-match';
import TenderRequestPage from './pages/learning-management-system/tender-request';
import LmsVideosPage from './pages/learning-management-system/lms-videos';
import TrainingDocumentPage from './pages/learning-management-system/training-document';
import LmsNewsPage from './pages/learning-management-system/lms-news';
import LmsNewsDetail from './pages/learning-management-system/lms-news/detail/LmsNewsDetail';
import LmsNewsManagementPage from './pages/learning-management-system/lms-news/management';
import LmsDashboardPage from './pages/learning-management-system/lms-dashboard';
import { LMS, LMS_ANY_VIEW, LMS_TRAINING_MANAGE, lmsAccess } from './constants/permissionCodes';
import DealersStockViewPage from './pages/DealersStockView';
import DealerPartsCatalog from './pages/dealerPartsCatalog';
import PaymentReportsPage from './pages/parts/paymentReports';
import CollectionReportPage from './pages/parts/collectionReport';
import ApprovalOrdersPage from './pages/approvalOrders';
import PreAccountingPage from './pages/accounting/preAccounting';
import CollateralReceivedPage from './pages/accounting/collateralReceived';
import TechnicalSupportRequestPage from './pages/quality/technical-support-request';
import VorPage from './pages/quality/vor';
import DealerInvoiceEntryPage from './pages/warranty-management/accounting-process/dealer-invoice-approval';
import ExternalItemDescriptionPage from './pages/parts/externalItemDescription';
import ExternalVehiclePage from './pages/externalVehicle';
import PendingApprovalRequestsPage from './pages/warranty-management/pending-approval-requests';
import OtherLaborPricePage from './pages/warranty-management/other-labor-price';
import VehicleWarrantyInformationPage from './pages/warranty-management/vehicle-warranty-information-reports';
import VehicleWarrantyClaimsReportPage from './pages/warranty-management/vehicle-warranty-claims-report';
import VehicleWarrantyClaimsReportSumPage from './pages/warranty-management/vehicle-warranty-claims-report-sum';
import ServiceBulletinReportPage from './pages/warranty-management/service-bulletin-report';
import ServiceBulletinDetail from './pages/warranty-management/service-bulletin-report/detail';
import BulletinImplementationReportPage from './pages/warranty-management/bulletin-implementation-report';
import VehicleParkReportPage from './pages/warranty-management/vehicle-park-report';
import CariDetailReportPage from './pages/accounting/cariDetailReport';
import CurrentAccountReportPage from './pages/dealer-management/currentAccountReport';
import LaborCatalogReportPage from './pages/warranty-management/labor-catalog-report';
import MatnrIntegrationStatusPage from './pages/parts/matnr-integration-status-report';
import DealerStockAddPage from './pages/management/dealerStockAdd';
import PartsDealerBrandMatchPage from './pages/management/partsDealerBrandMatch';
import KarsanStockVehiclesReport from './pages/karsan-stock-vehicles-report';
import ClaimHistoryReportPage from './pages/warranty-management/claim-history-report';
import PreApprovalHistoryReportPage from './pages/warranty-management/pre-approval-history-report';
import DealerProcessingReportPage from './pages/warranty-management/dealer-processing-report';
import RepairSlipReportPage from './pages/warranty-management/repair-slip-report';
import BlockShelfSeqItemDescriptionPage from './pages/blockShelfSeqItemDescription';
import VehicleServiceReportPage from './pages/warranty-management/vehicle-service-entry-report';
import MaterialCatalogPage from './pages/parts/material-catalog';
import VehicleDistributionReportPage from './pages/vehicleSales/vehicleDistributionReport';
import VehicleSaleReportPage from './pages/vehicleSales/vehicleSaleReport';
import MatnrPartPricePage from './pages/parts/matnr-parts-price-report';
import WarrantyAuditCriteriaManagementPage from './pages/warranty-management/warranty-audit/criteria-management';
import WarrantyAuditFormListPage from './pages/warranty-management/warranty-audit/audit-form';
import WarrantyAuditFormCreateEdit from './pages/warranty-management/warranty-audit/audit-form/createEditForm/AuditFormCreateEdit';
import TeamProductivityMeasurement from './pages/team-productivity-measurement';
import TeamProductivityMeasurementCreateEdit from './pages/team-productivity-measurement/createEditForm';

// Inner App component that has access to Redux hooks
const AppContent = () => {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const toastRef = useRef(null);

    useEffect(() => {
        // Toast referansını toastService'e aktar
        if (toastRef.current) {
            toastService.setToastRef(toastRef);
        }
    }, []);

    // Check token expiration on app startup
    // If token is expired, logout silently and redirect to login
    const isTokenExpired = useSelector(selectIsTokenExpired);

    useEffect(() => {
        if (isAuthenticated && isTokenExpired) {
            dispatch(logout());
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync logout across browser tabs
    // When user logs out in one tab, all other tabs will also logout
    useEffect(() => {
        const handleStorageChange = (event) => {
            // Listen for explicit logout signal from other tabs
            // Only process if we're currently authenticated (prevents infinite loop)
            if (event.key === 'logout-event' && event.newValue && isAuthenticated) {
                dispatch(logout());
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [dispatch, isAuthenticated]);

    return (
        <PrimeReactProvider>
            <LanguageProvider>
                <MenuCategoryProvider>
                    <Toast ref={toastRef} />
                    <LayoutProvider>
                        <MenuProvider>
                            <Routes>
                                {/* Auth Routes */}
                                <Route element={<FullPageLayout />}>
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/forgot-password" element={<ForgotPassword />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                </Route>

                                {/* Error Routes */}
                                <Route element={<FullPageLayout />}>
                                    <Route path="/403" element={<ForbiddenPage />} />
                                </Route>

                                {/* Protected Routes */}
                                <Route
                                    path="/"
                                    element={
                                        <PrivateRoute>
                                            <Layout />
                                        </PrivateRoute>
                                    }
                                >
                                    <Route index element={<MenuCategory />} />
                                    <Route path="user-params" element={<UserParams />} />
                                    <Route
                                        path="management/brand"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <BrandPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/announcement"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <AnnouncementPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/announcement-type"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <AnnouncementTypePage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="announcements/dealer-announcements"
                                        element={
                                            <ProtectedRoute permissions={{ requiresPartTeam: true, requiresSalesDealer: true, requiresServiceDealer: true, requiresWarrantyTeam: true }}>
                                                <DealerAnnouncementsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="management/model"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <ModelPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="management/customer-address-price" element={<CustomerAddressPricePage />} />
                                    <Route path="management/customer-order-type" element={<CustomerOrderTypePage />} />

                                    {/* Birleşik Bayi Kaydı + Dinamik N-Adımlı Onay Akışı */}
                                    <Route
                                        path="management/dealer-registration"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <DealerRegistrationPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/dealer-registration/new"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <DealerRegistrationEditPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/dealer-registration/dealer/:sapDealerId"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <DealerRegistrationEditPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/dealer-registration/edit/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <DealerRegistrationEditPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/dealer-approval"
                                        element={
                                            <ProtectedRoute permissions={{ roles: ['WarrantyManager', 'SSHManager', 'Admin'] }}>
                                                <DealerApprovalInboxPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/dealer-approval/detail/:id"
                                        element={
                                            <ProtectedRoute permissions={{ roles: ['WarrantyManager', 'SSHManager', 'Admin'] }}>
                                                <DealerApprovalDetailPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    {/* <Route path="management/system-definitions/system-descriptions" element={<SystemDescriptionsPage />} /> */}
                                    <Route
                                        path="management/system-definitions/system-items"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <SystemItemsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/user-authorization-menu/user-definitions/system-administrator"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <SystemAdministratorPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/user-authorization-menu/user-definitions/user-accounts"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <UserAccountsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/user-authorization-menu/user-definitions/super-admin-definition"
                                        element={
                                            <ProtectedRoute permissions={{ requiresManagePermission: 'UpdateRole' }}>
                                                <SuperAdminDefinitionPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/authorization-v2/roles"
                                        element={
                                            <ProtectedRoute permissions={{ requiresManagePermission: 'UpdateRole', requiresPermissionApi: true }}>
                                                <AuthorizationV2RolesPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/authorization-v2/users"
                                        element={
                                            <ProtectedRoute permissions={{ requiresManagePermission: 'UpdateRole', requiresPermissionApi: true }}>
                                                <AuthorizationV2UsersPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="management/dealer-stock-add"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAdminOnly: true }}>
                                                <DealerStockAddPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="management/parts-dealer-brand-match"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAdminOnly: true }}>
                                                <PartsDealerBrandMatchPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Garanti Onay Mekanizması (warranty-approval-management) */}
                                    <Route
                                        path="warranty-management/warranty-approval-management/approval-groups"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <ApprovalGroupsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-approval-management/approval-limits"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <ApprovalLimitsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="reports/order-control-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <OrderControlReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="reports/customer-address-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <CustomerAddressReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="reports/order-summary"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <OrderSummaryPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="reports/parts-balance-list"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <PartsBalanceListPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="reports/shipments-deliveries"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <ShipmentsDeliveriesPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="reports/estimated-delivery-date"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <EstimatedDeliveryDatePage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="dealersStockView"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresPartTeam: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <DealersStockViewPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Bayi Yönetim Sayfaları (dealer-management)*/}

                                    <Route
                                        path="/dealer-management/dealer-warehouse"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresKarsanLogistics: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <DealerWarehousePage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/dealer-management/external-vehicle-definition"
                                        element={
                                            <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <ExternalVehiclePage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/dealer-management/customer-definition"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresKarsanLogistics: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <CustomerDefinitionPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/dealer-management/dealer-stock-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAdminOnly: true }}>
                                                <DealerStockReportPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/dealer-management/current-account-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <CurrentAccountReportPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/parts/dealer-parts-return-process"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresKarsanLogistics: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <DealerPartsReturnProcessPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/dealer-management/package-definitions"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <PackageDefinitionsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/parts/parts-catalog"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true }}>
                                                <DealerPartsCatalog />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/parts/payment-reports"
                                        element={
                                            <ProtectedRoute permissions={{ requiresPartTeam: true, requiresSalesDealer: true, requiresDomesticDealer: true }}>
                                                <PaymentReportsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/parts/collection-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAccounting: true, requiresFinanceTeam: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <CollectionReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/parts/matnr-integration-status"
                                        element={
                                            <ProtectedRoute permissions={{ requiresPartTeam: true, excludeDealerRoles: true }}>
                                                <MatnrIntegrationStatusPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/parts/material-catalog"
                                        element={
                                            <ProtectedRoute permissions={{ requiresPartTeam: true, requiresSalesDealer: true, requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <MaterialCatalogPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/parts/unpicked-parts-report"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresPartTeam: true,
                                                    excludeDealerRoles: true,
                                                    excludeRoles: ['KarsanSSH']
                                                }}
                                            >
                                                <UnpickedPartsReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/parts/matnr-parts-price-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <MatnrPartPricePage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    {/* Garanti Yönetimi (warranty-management) */}
                                    <Route
                                        path="warranty-management/dashboard"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <WarrantyDashboardPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    {/* Learning Management System - yetki kodu VEYA eski kural (bkz. lmsAccess) */}
                                    <Route
                                        path="learning-management-system/dashboard"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess(LMS_ANY_VIEW)}>
                                                <LmsDashboardPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/my-trainings"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.TRAINING_VIEW, LMS.REQUEST_VIEW, LMS.REPORT_VIEW])}>
                                                <MyTrainingsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/management-pages"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.DEFINITION_VIEW, ...LMS_TRAINING_MANAGE, LMS.NEWS_VIEW])}>
                                                <LmsManagementPagesPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="learning-management-system/library" element={<Navigate to="/learning-management-system/library/videos" replace />} />
                                    {/* LMS Haberler - herkes */}
                                    <Route path="learning-management-system/news" element={<LmsNewsPage />} />
                                    <Route path="learning-management-system/news/:id" element={<LmsNewsDetail />} />
                                    <Route
                                        path="learning-management-system/news-management"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.NEWS_VIEW])}>
                                                <LmsNewsManagementPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/library/videos"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess(LMS_ANY_VIEW)}>
                                                <LmsVideosPage readOnly />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/library/documents"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess(LMS_ANY_VIEW)}>
                                                <TrainingDocumentPage readOnly />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-type-definitions"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.DEFINITION_VIEW])}>
                                                <TrainingTypeDefinitionsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-location-definitions"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.DEFINITION_VIEW])}>
                                                <TrainingLocationDefinitionsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/precondition-definitions"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.DEFINITION_VIEW])}>
                                                <PreconditionDefinitionsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/vehicle-model-definitions"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.DEFINITION_VIEW])}>
                                                <VehicleModelDefinitionsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-catalog"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.TRAINING_VIEW])}>
                                                <TrainingCatalogPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-catalog/create"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.TRAINING_CREATE])}>
                                                <TrainingCatalogCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-catalog/:id"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.TRAINING_VIEW])}>
                                                <TrainingCatalogCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-planning"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.TRAINING_VIEW])}>
                                                <TrainingPlanningPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-report"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.REPORT_VIEW])}>
                                                <TrainingReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-report/create"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.REPORT_CREATE])}>
                                                <TrainingReportCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-report/:id"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.REPORT_VIEW])}>
                                                <TrainingReportCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-request"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.REQUEST_VIEW])}>
                                                <TrainingRequestPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/vehicle-training-match"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess(LMS_TRAINING_MANAGE)}>
                                                <VehicleTrainingMatchPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/tender-request"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess([LMS.REQUEST_VIEW])}>
                                                <TenderRequestPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/lms-videos"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess(LMS_TRAINING_MANAGE)}>
                                                <LmsVideosPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="learning-management-system/training-document"
                                        element={
                                            <ProtectedRoute permissions={lmsAccess(LMS_TRAINING_MANAGE)}>
                                                <TrainingDocumentPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-requests/pre-approval"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <PreApprovalPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-requests/claims"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <WarrantyClaims />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-requests/claims/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <WarrantyClaimsCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-requests/claims/create"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <WarrantyClaimsCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-requests/customer-declaration"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <CustomerDeclarationPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Garanti Denetim Süreci (warranty-audit) */}
                                    <Route
                                        path="warranty-management/warranty-audit/criteria"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAdminOnly: true }}>
                                                <WarrantyAuditCriteriaManagementPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-audit/form"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAftersalesUser: true, requiresWarrantyTeam: true }}>
                                                <WarrantyAuditFormListPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-audit/form/create"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAftersalesUser: true, requiresWarrantyTeam: true }}>
                                                <WarrantyAuditFormCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-audit/form/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresAftersalesUser: true, requiresWarrantyTeam: true }}>
                                                <WarrantyAuditFormCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="warranty-management/warranty-settings/labor-catalog"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <LaborCatalogPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-settings/fault-customer-definitions"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <FaultCustomerDefinitionsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="dealer-management/other-labor-price"
                                        element={
                                            <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresServiceDealer: true, requiresWarrantyTeam: true }}>
                                                <OtherLaborPricePage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Onay Bekleyen Talepler */}
                                    <Route
                                        path="warranty-management/pending-approval-requests"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true, excludeRoles: ['WarrantyTeam'] }}>
                                                <PendingApprovalRequestsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Araç Garanti Süresi (vehicle-warranty-period) */}
                                    <Route
                                        path="warranty-management/vehicle-warranty-period/main"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <VehicleWarrantyPeriodPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/vehicle-warranty-period/additional"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <VehicleAdditionalWarrantyPeriodPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/vehicle-warranty-period/claim-limit"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <WarrantyClaimLimitPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/detail-query"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeRoles: ['Dealer', 'SalesDealer', 'ServiceDealer'] }}>
                                                <VehicleDetailQueryPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/*   Garanti Süreci Formları (warranty-process-forms) */}
                                    <Route
                                        path="warranty-management/warranty-process-forms/vehicle-acceptance-form"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true, requiresDomesticDealer: true }}>
                                                <VehicleAcceptanceFormPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-process-forms/pre-stock-inspection-checklist"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <PreStockInspectıonChecklistPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-process-forms/pre-stock-inspection-checklist/create"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <FormPreStockInspection />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-process-forms/pre-stock-inspection-checklist/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <FormPreStockInspection />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-process-forms/dealer-vehicle-registration-form"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true, requiresDomesticDealer: true }}>
                                                <DealerVehicleRegistrationFormPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-process-forms/dealer-vehicle-registration-form/create"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true, requiresDomesticDealer: true }}>
                                                <VehicleRegistrationFormCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-process-forms/dealer-vehicle-registration-form/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true, requiresDomesticDealer: true }}>
                                                <VehicleRegistrationFormCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warranty-process-forms/warranty-date-change-approval"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <WarrantyDateChangeApprovalPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Accounting Process */}
                                    <Route
                                        path="warranty-management/accounting-process/invoice-approval-form"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <InvoiceApprovalPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/accounting-process/dealer-invoice-entry"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <DealerInvoiceEntryPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/order-management/order-list"
                                        element={
                                            <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                <OrderListPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/order-management/order-list/create"
                                        element={
                                            <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                <OrderListCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/order-management/order-list/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                <OrderListCreateEdit />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="/order-management/order-details" element={<OrderDetails />} />

                                    {/* Shipments and Deliveries Report */}
                                    <Route
                                        path="/shipments/shipment-delivery"
                                        element={
                                            <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                <ShipmentDeliveryPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Top Price Parts Report */}
                                    <Route path="/reports/top-price-parts" element={<TopPricePartsReport />} />

                                    {/* Daily Inactive Vehicle Report */}
                                    <Route
                                        path="/quality/reports/daily-inactive-vehicle"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <DailyInactiveVehicleReport />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Geri Bildirim Formu Raporu */}
                                    <Route
                                        path="/quality/reports/feedback-form"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <FeedbackFormReportPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                        {/*   Arıza Kodu Tanımlamaları (fault-codes) */}
                                        <Route
                                            path="/warranty-management/fault-codes/:categoryLevelUrl?" // categoryLevelUrl opsiyonel
                                            element={
                                                <ProtectedRoute permissions={{ requiresAdminOnly: true }}>
                                                    <FaultCodesDefinitionsPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        {/*   Part sayfaları */}
                                        <Route
                                            path="parts/waybill-shipment" // categoryLevelUrl opsiyonel
                                            element={<WaybillShipmentPage />}
                                        />
                                        <Route
                                            path="parts/order-pickup" // categoryLevelUrl opsiyonel
                                            element={
                                                <ProtectedRoute permissions={{ requiresPartTeam: true, requiresSalesDealer: true, requiresWarrantyTeam: true }}>
                                                    <OrderPickUpPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="parts/retail-sales"
                                            element={
                                                <ProtectedRoute permissions={{ requiresPartTeam: true, requiresSalesDealer: true, requiresWarrantyTeam: true }}>
                                                    <RetailSalesPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="vehicle-sales/place-order"
                                            element={
                                                <ProtectedRoute permissions={{ requiresDomesticDealer: true }}>
                                                    <PlaceOrderPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="vehicle-sales/order-requests"
                                            element={
                                                <ProtectedRoute permissions={{ requiresDomesticDealer: true }}>
                                                    <OrderRequestsPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="vehicle-sales/my-vehicle-stock"
                                            element={
                                                <ProtectedRoute permissions={{ requiresDomesticDealer: true }}>
                                                    <MyVehicleStockPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                     <Route
                                            path="vehicle-sales/karsan-stock-vehicles"
                                            element={
                                                <ProtectedRoute permissions={{ requiresKarsanLogistics: true, requiresVehicleSalesManager: true, requiresSalesDealer: true, requiresAdmin: true }}>
                                                       <KarsanStockVehiclesReport />
                                                </ProtectedRoute>
                                          }
                                        />
                                     <Route
                                            path="vehicle-sales/vehicle-distribution-report"
                                            element={
                                                <ProtectedRoute permissions={{ requiresVehicleSalesManager: true, requiresKarsanLogistics: true, requiresAdmin: true }}>
                                                    <VehicleDistributionReportPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                     <Route
                                            path="vehicle-sales/vehicle-sale-report"
                                            element={
                                                <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresVehicleSalesManager: true, requiresAdmin: true }}>
                                                    <VehicleSaleReportPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="vehicle-sales/sell-to-customer"
                                            element={
                                                <ProtectedRoute permissions={{ requiresDomesticDealer: true }}>
                                                    <SellToCustomerPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="vehicle-sales/for-sale"
                                            element={
                                                <ProtectedRoute permissions={{ requiresDomesticDealer: true }}>
                                                    <VehiclesForSalePage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="vehicle-sales/refund-orders"
                                            element={
                                                <ProtectedRoute permissions={{ requiresDomesticDealer: true }}>
                                                    <RefundOrdersPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="parts/internal-retail-parts"
                                            element={
                                                <ProtectedRoute permissions={{ requiresForeignDealer: true  }}>
                                                    <InternalRetailPartsPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/parts/credit-card-collection" 
                                            element={
                                                <ProtectedRoute permissions={{ requiresPartTeam: true, requiresSalesDealer: true, requiresServiceDealer: true, requiresWarrantyTeam: true, requiresDomesticDealer: true, requiresFinanceTeam: true }}>
                                                    <CreditCardCollectionPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/parts/credit-card-collection/success"
                                            element={<PaymentSuccess />}
                                        />
                                        <Route
                                            path="/parts/credit-card-collection/failure"
                                            element={<PaymentFailure />}
                                        />
                                        <Route
                                            path="/parts/credit-card-collection/result"
                                            element={<PaymentResult />}
                                        />
                                        <Route
                                            path="parts/order-balance" // categoryLevelUrl opsiyonel
                                            element={<OrderBalancePage />}
                                        />
                                        <Route
                                            path="/parts/product-search"
                                            element={
                                                <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                    <ProductSearchPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/dealer-management/external-item-description"
                                            element={
                                                <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                    <ExternalItemDescriptionPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/dealer-management/block-shelf-seq-item-description"
                                            element={
                                                <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                    <BlockShelfSeqItemDescriptionPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/basket"
                                            element={
                                                <ProtectedRoute permissions={{ requiresSalesDealer: true, requiresPartTeam: true, requiresWarrantyTeam: true }}>
                                                    <BasketPage />
                                                </ProtectedRoute>
                                            }
                                        />

                                    <Route
                                        path="/dealer-management/dealerUserDefinition"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresKarsanLogistics: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <DealerUserAccountPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/accounting/cari-detail-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresKarsanLogistics: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                <CariDetailReportPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/full-service-invoices"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresCanUseFullService: true, requiresServiceDealer: true }}>
                                                <FullServiceInvoicePage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/full-service/create-edit/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresCanUseFullService: true, requiresServiceDealer: true }}>
                                                <FullServiceCreateEditPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/full-service/create-edit"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresCanUseFullService: true, requiresServiceDealer: true }}>
                                                <FullServiceCreateEditPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/warranty-management/warranty-reports"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <WarrantyReportsPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/vehicle-warranty-information"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresWarrantyTeam: true,
                                                    excludeRoles: ['Dealer', 'SalesDealer', 'ServiceDealer', 'KarsanSSH', 'PartTeam', 'KarsanLogistics', 'WarrantyQualityOfficer']
                                                }}
                                            >
                                                <VehicleWarrantyInformationPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/vehicle-warranty-claims"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresWarrantyTeam: true,
                                                    requiresServiceDealer: true,
                                                    requiresKmobilityUser: true
                                                }}
                                            >
                                                <VehicleWarrantyClaimsReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/vehicle-warranty-claims-sum"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    roles: ['WarrantyTeam', 'WarrantyManager', 'Admin'],
                                                    excludeDealerRoles: true,
                                                    excludeRoles: ['SSHManager', 'SSHDirector', 'SSHGeneralManager', 'CEO', 'RegionalManager', 'KarsanSSH', 'PartTeam']
                                                }}
                                            >
                                                <VehicleWarrantyClaimsReportSumPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/vehicle-park-report"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresWarrantyTeam: true,
                                                    excludeDealerRoles: true,
                                                    excludeRoles: ['SSHManager', 'SSHDirector', 'SSHGeneralManager', 'CEO', 'RegionalManager', 'KarsanSSH']
                                                }}
                                            >
                                                <VehicleParkReportPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="warranty-management/warrantyReports/claim-history-report"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresWarrantyTeam: true,
                                                    excludeRoles: ['SSHManager', 'SSHDirector', 'SSHGeneralManager', 'CEO', 'RegionalManager', 'KarsanSSH']
                                                }}
                                            >
                                                <ClaimHistoryReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/bulletins/service-bulletin-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true, requiresServiceBulletinUser: true }}>
                                                <ServiceBulletinReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/warranty-management/bulletins/service-bulletin-report/:bulletinNo"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true, requiresServiceBulletinUser: true }}>
                                                <ServiceBulletinDetail />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/bulletins/bulletin-implementation-report"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    roles: ['WarrantyTeam', 'WarrantyManager', 'Admin', 'ServiceBulletinUser'],
                                                    excludeDealerRoles: true
                                                }}
                                            >
                                                <BulletinImplementationReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/team-productivity"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresWarrantyTeam: true,
                                                    excludeRoles: ['SSHManager', 'SSHDirector', 'SSHGeneralManager', 'CEO', 'RegionalManager']
                                                }}
                                            >
                                                <TeamProductivityMeasurement />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="warranty-management/warrantyReports/labor-catalog"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <LaborCatalogReportPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="warranty-management/warrantyReports/vehicle-service-entry-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <VehicleServiceReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/dealer-processing-report"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresServiceDealer: true }}>
                                                <DealerProcessingReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/repair-slip-report"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    roles: ['WarrantyTeam', 'WarrantyManager', 'Admin'],
                                                    excludeDealerRoles: true,
                                                    excludeRoles: ['SSHManager', 'SSHDirector', 'SSHGeneralManager', 'CEO', 'RegionalManager', 'KarsanSSH', 'PartTeam']
                                                }}
                                            >
                                                <RepairSlipReportPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="warranty-management/warrantyReports/pre-approval-history-report"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresWarrantyTeam: true,
                                                    excludeRoles: ['SSHManager', 'SSHDirector', 'SSHGeneralManager', 'CEO', 'RegionalManager', 'KarsanSSH']
                                                }}
                                            >
                                                <PreApprovalHistoryReportPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/quality/technical-support-request"
                                        element={
                                            <ProtectedRoute
                                                permissions={{
                                                    requiresWarrantyQualityOfficer: true,
                                                    requiresServiceDealer: true,
                                                    requiresSalesDealer: true,
                                                    requiresWarrantyTeam: true,
                                                    requiresTechnicalSupportUser: true
                                                }}
                                            >
                                                <TechnicalSupportRequestPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/quality/vor"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyQualityOfficer: true, excludeDealerRoles: true }}>
                                                <VorPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/part/set-labors"
                                        element={
                                            <ProtectedRoute permissions={{ requiresWarrantyTeam: true, excludeDealerRoles: true }}>
                                                <PartLaborMappingPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/orders/approval-orders"
                                        element={
                                            <ProtectedRoute permissions={{ requiresApprovalOrdersDealer: true }}>
                                                <ApprovalOrdersPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/proforma-invoice"
                                        element={
                                            <ProtectedRoute permissions={{ requiresServiceDealer: true }}>
                                                <ProformaInvoicePage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/proforma-invoice/new"
                                        element={
                                            <ProtectedRoute permissions={{ requiresServiceDealer: true }}>
                                                <ProformaInvoiceDetailPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/proforma-invoice/:id"
                                        element={
                                            <ProtectedRoute permissions={{ requiresServiceDealer: true }}>
                                                <ProformaInvoiceDetailPage />
                                            </ProtectedRoute>
                                        }
                                    />
                                    {/* Servis Randevusu geçici olarak kapatıldı
                        <Route
                            path="/service-appointment"
                            element={
                                 <ProtectedRoute permissions={{ requiresServiceDealer: true, excludeRoles: ["KarsanSSH"] }}>
                                    <ServiceAppointmentPage />
                                </ProtectedRoute>
                            }
                        />
                        */}
                        <Route
                            path="/credit-card-collection"
                            element={<CreditCardCollectionPage />}
                        />
                                        <Route
                                            path="accounting/pre-accounting"
                                            element={
                                                <ProtectedRoute permissions={{ requiresWarrantyTeam: true, requiresSalesDealer: true, requiresServiceDealer: true }}>
                                                    <PreAccountingPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="accounting/collateral-received"
                                            element={
                                                <ProtectedRoute permissions={{ requiresManagePermission: 'UpdateRole', orRoles: ['FinanceTeam'] }}>
                                                    <CollateralReceivedPage />
                                                </ProtectedRoute>
                                            }
                                        />
                                    </Route>
                                </Routes>
                            </MenuProvider>
                        </LayoutProvider>
                    </MenuCategoryProvider>
                </LanguageProvider>
            </PrimeReactProvider>
    );
};

// Main App component with Redux Provider
const App = () => {
    return (
        <Provider store={store}>
            <AppContent />
        </Provider>
    );
};

export default App;
